import {
  beginCopilotUiReadiness,
  captureUiReadinessTime,
  type CopilotUiReadinessView,
  failPendingCopilotUiInitialization,
  isUiReadinessEnabled,
} from '@github-ui/copilot-browser-latency/copilot-ui-readiness'
import {
  CopilotUiReadinessContext,
  useCopilotUiReadinessLifetime,
} from '@github-ui/copilot-browser-latency/copilot-ui-readiness-context'
import {logCopilotChatRouting, type OpenCopilotChatEvent} from '@github-ui/copilot-chat-events'
import {getCopilotChatPolicyVersion} from '@github-ui/copilot-chat-helpers/constants'
import type {CopilotDeferredPayload} from '@github-ui/copilot-chat-types'
import {DEFERRED_PAYLOAD_DEFAULTS} from '@github-ui/copilot-chat-types'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {CopilotImmersivePayload} from '@github-ui/copilot-immersive-v1/routes/payloads'
import {isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

export type CopilotChatLoaderProps = {
  uiReadiness?: CopilotUiReadinessView
  copilotChatUrl: string
  pendingOpenEvent: OpenCopilotChatEvent | null
  onPendingEventHandled: () => void
  chatAppOpen?: boolean
  onChatAppOpen?: () => void
  onChatAppClose?: () => void
  disableAutoFocus?: boolean
}

// Keep the payload query and lazy chat surfaces isolated from the global navigation header.
export function CopilotChatLoader({
  uiReadiness: suppliedUiReadiness,
  copilotChatUrl,
  pendingOpenEvent,
  onPendingEventHandled,
  chatAppOpen,
  onChatAppOpen,
  onChatAppClose,
  disableAutoFocus,
}: CopilotChatLoaderProps) {
  const [restoredReadiness, setRestoredReadiness] = useState<CopilotUiReadinessView>()
  const [restoreObserved, setRestoreObserved] = useState(false)
  const restoreObservedRef = useRef(false)
  const uiReadiness = suppliedUiReadiness ?? restoredReadiness
  const needsRestoreObservation = Boolean(chatAppOpen) && !uiReadiness && !restoreObserved && isUiReadinessEnabled()
  useCopilotUiReadinessLifetime(restoredReadiness)
  useLayoutEffect(() => {
    if (!needsRestoreObservation || restoreObservedRef.current) return
    restoreObservedRef.current = true
    const view = beginCopilotUiReadiness({
      surface: 'embedded_chat',
      entryKind: 'resume',
      repositoryScope: 'unknown',
    })
    setRestoredReadiness(view)
    setRestoreObserved(true)
  }, [needsRestoreObservation])
  useEffect(() => {
    if (chatAppOpen || !restoredReadiness) return
    restoredReadiness.dispose()
    restoreObservedRef.current = false
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Release telemetry when the externally controlled panel closes.
    setRestoredReadiness(undefined)
    setRestoreObserved(false)
  }, [chatAppOpen, restoredReadiness])
  const payloadReadinessTime = useRef<ReturnType<typeof captureUiReadinessTime>>(undefined)
  const deferredReadinessTime = useRef<ReturnType<typeof captureUiReadinessTime>>(undefined)
  const useChatApp = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')
  const quickChatOwnsDiffEvents = useChatApp
  const [hasLoadedChatApp, setHasLoadedChatApp] = useState(false)

  if (chatAppOpen && !hasLoadedChatApp) {
    setHasLoadedChatApp(true)
  }

  const {
    data: copilotChatProps,
    error,
    isError,
    isFetching,
    isPending,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Receipt timing is telemetry, not payload cache identity.
  } = useQuery({
    queryKey: ['copilot-chat', copilotChatUrl],
    queryFn: async () => {
      const response = await verifiedFetchJSON(copilotChatUrl)
      logCopilotChatRouting('chat payload response received', {
        ok: response.ok,
        status: response.status,
      })

      if (response.ok) {
        try {
          return (await response.json()) as CopilotImmersivePayload & {copilotWebIntegrationCutover?: boolean}
        } catch {
          throw new Error('Failed to parse copilot chat response.')
        } finally {
          payloadReadinessTime.current = captureUiReadinessTime()
        }
      }
      throw new Error('Failed to retrieve copilot chat payload.')
    },
  })

  // When the server provides a deferredPayloadUrl, merge deferred defaults into the lite payload.
  // The actual cache-dependent values will be fetched async when the panel opens.
  const embeddedPayload = useMemo(() => {
    if (!copilotChatProps) return null
    if (copilotChatProps.deferredPayloadUrl) {
      return {
        ...DEFERRED_PAYLOAD_DEFAULTS,
        ...copilotChatProps,
      }
    }
    return copilotChatProps
  }, [copilotChatProps])

  // Fetch the deferred (cache-dependent) payload here so the ChatApp branch
  // gets the resolved entitlement fields when it opens. Immersive runs the
  // same query inside `useDeferredPayload`; sharing the `queryKey` means
  // react-query dedupes the request and both surfaces read from the same
  // cache entry. Without this the ChatApp `EntitlementProvider` would only
  // ever see the `DEFERRED_PAYLOAD_DEFAULTS` (undefined `quotas`), keeping
  // the token-usage meter hidden for users whose entitlement query isn't
  // enabled.
  const deferredPayloadUrl = embeddedPayload?.deferredPayloadUrl
  // Receipt metadata does not affect the fetched payload or its cache identity.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps, @github-ui/github-monorepo/require-query-persister
  const deferredPayloadQuery = useQuery({
    queryKey: ['copilot-deferred-payload', deferredPayloadUrl],
    queryFn: async () => {
      const response = await verifiedFetchJSON(deferredPayloadUrl as string)
      if (!response.ok) {
        throw new Error(`Deferred payload fetch failed (${response.status})`)
      }
      const payload = (await response.json()) as CopilotDeferredPayload
      const receivedAt = captureUiReadinessTime()
      deferredReadinessTime.current = receivedAt
      uiReadiness?.configurationAvailable(receivedAt, true)
      return payload
    },
    enabled: Boolean(deferredPayloadUrl) && Boolean(chatAppOpen),
    staleTime: Infinity,
    retry: 2,
  })
  const {data: deferredPayload, isError: isDeferredError, isFetching: isDeferredFetching} = deferredPayloadQuery

  useEffect(() => {
    if (
      (isError && !isFetching && !copilotChatProps) ||
      (chatAppOpen && deferredPayloadUrl && isDeferredError && !isDeferredFetching && !deferredPayload)
    ) {
      uiReadiness?.failure('bootstrap')
    }
  }, [
    chatAppOpen,
    copilotChatProps,
    deferredPayload,
    deferredPayloadUrl,
    isDeferredError,
    isDeferredFetching,
    isError,
    isFetching,
    uiReadiness,
  ])

  useEffect(() => {
    if (!uiReadiness || !embeddedPayload || (deferredPayloadUrl && !deferredPayload)) return
    // Cache hits skip queryFn, so observe adoption on this view's local clock.
    const time =
      (deferredPayloadUrl ? deferredReadinessTime.current : payloadReadinessTime.current) ?? captureUiReadinessTime()
    if (time) uiReadiness.configurationAvailable(time, true)
  }, [uiReadiness, embeddedPayload, deferredPayloadUrl, deferredPayload])

  // Prefer the deferred payload's entitlement fields once resolved. Before it
  // arrives the ChatApp is seeded with the same defaults the immersive UI
  // renders with, so the meter simply appears the moment the fetch settles.
  const chatAppEntitlement = useMemo(() => {
    if (!embeddedPayload) return null
    const source = deferredPayload ?? embeddedPayload
    return {
      licenseType: source.licenseType,
      plan: source.plan,
      quotas: source.quotas,
      hasCEorCBAccess: source.hasCEorCBAccess,
    }
  }, [embeddedPayload, deferredPayload])

  // Account context the ChatApp panel's new-session composer needs before a
  // session exists: who the draft is scoped for, which organizations it may be
  // scoped to, and the quota and MCP gate that decide whether it can be
  // composed at all. The panel supplies the scope control itself; this only
  // decides whether there is anything to scope to.
  const chatAppNewSession = useMemo(() => {
    if (!embeddedPayload) return undefined
    return {
      currentUserLogin: embeddedPayload.currentUserLogin,
      currentUserAvatarUrl: embeddedPayload.currentUser?.primaryAvatarUrl,
      availableOrganizations: embeddedPayload.userOrganizations,
      // The quota is cache-dependent, so on the deferred path it lands with the
      // deferred fetch rather than the lite payload. Reading only the lite one
      // would leave composing unblocked for an account that has run out.
      agentQuota: deferredPayload?.agentQuotaSnapshot ?? embeddedPayload.agentQuotaSnapshot,
      mcpPermissionGateEnabled: deferredPayload?.mcpPermissionGateEnabled ?? embeddedPayload.mcpPermissionGateEnabled,
      enterpriseSandboxEnabled: embeddedPayload.enterpriseSandboxEnabled,
    }
  }, [embeddedPayload, deferredPayload])

  // Use the same surface-selection rules as CopilotItems. Payload-shape checks
  // diverge for source-less events (including Ask Copilot and actions prompts)
  // and can ignore a retained dock owner, causing one surface to open while the
  // other consumes and acknowledges the event.
  const routePendingEventToQuickChat =
    pendingOpenEvent !== null &&
    getCopilotChatPolicyVersion({
      chatAppEmbeddedEnabled: useChatApp,
      source: pendingOpenEvent.payload.source,
    }) === 'v2'
  const shouldMountChatApp = !needsRestoreObservation && (hasLoadedChatApp || routePendingEventToQuickChat)

  useEffect(() => {
    logCopilotChatRouting('loader state changed', {
      chatAppEmbeddedEnabled: useChatApp,
      ownsDiffEventsEnabled: quickChatOwnsDiffEvents,
      chatAppOpen: Boolean(chatAppOpen),
      hasLoadedChatApp,
      pendingEventId: pendingOpenEvent?.payload.id,
      pendingEventIntent: pendingOpenEvent?.payload.intent,
      pendingEventSource: pendingOpenEvent?.payload.source,
      routePendingEventToQuickChat,
      shouldMountChatApp,
      payloadPending: isPending,
      payloadPresent: Boolean(embeddedPayload),
      payloadError: isError ? (error instanceof Error ? error.message : 'Unknown error') : undefined,
      deferredPayloadPresent: Boolean(deferredPayload),
      dockedTargetPresent: Boolean(document.getElementById('copilot-chat-docked-content')),
    })
  }, [
    chatAppOpen,
    deferredPayload,
    embeddedPayload,
    error,
    hasLoadedChatApp,
    isError,
    isPending,
    pendingOpenEvent,
    quickChatOwnsDiffEvents,
    routePendingEventToQuickChat,
    shouldMountChatApp,
    useChatApp,
  ])

  if (isPending || !embeddedPayload) return null

  if (useChatApp) {
    return (
      <>
        <Suspense fallback={null}>
          <LazyCopilotImmersiveEmbedded
            payload={embeddedPayload}
            pendingOpenEvent={routePendingEventToQuickChat ? null : pendingOpenEvent}
            onPendingEventHandled={routePendingEventToQuickChat ? () => {} : onPendingEventHandled}
          />
        </Suspense>
        {shouldMountChatApp ? (
          <Suspense fallback={null}>
            <CopilotUiReadinessContext value={uiReadiness}>
              <LazyChatAppEmbedded
                ahpConfig={embeddedPayload.ahpConfig}
                apiURL={embeddedPayload.apiURL}
                copilotWebIntegrationCutover={embeddedPayload.copilotWebIntegrationCutover}
                ssoOrganizations={embeddedPayload.ssoOrganizations}
                realIp={embeddedPayload.realIp}
                apiVersion={embeddedPayload.apiVersion}
                personalInstructions={embeddedPayload.personalInstructions}
                customInstructions={embeddedPayload.customInstructions}
                open={chatAppOpen}
                // While immersive embedded is co-mounted it owns the shared diff and
                // new-thread events; quick chat only takes ownership when this flag
                // flips (i.e. once it replaces immersive), so it's off by default.
                ownsSharedEvents={quickChatOwnsDiffEvents}
                pendingOpenEvent={routePendingEventToQuickChat ? pendingOpenEvent : null}
                onPendingOpenEventHandled={routePendingEventToQuickChat ? onPendingEventHandled : undefined}
                onRequestOpen={onChatAppOpen}
                onClose={onChatAppClose}
                disableAutoFocus={disableAutoFocus}
                // Seed the ChatApp composer's entitlement provider from the same
                // payload the immersive UI uses so the token-usage meter button
                // renders inside the ChatApp panel without an extra fetch. When
                // the lite payload is served with a `deferredPayloadUrl`, this
                // reflects the shared deferred fetch above once it resolves —
                // otherwise `licenseType`/`quotas` here would just be the
                // `DEFERRED_PAYLOAD_DEFAULTS` and the meter would never appear
                // for users whose entitlement query isn't enabled.
                entitlement={chatAppEntitlement ?? undefined}
                newSession={chatAppNewSession}
              />
            </CopilotUiReadinessContext>
          </Suspense>
        ) : null}
      </>
    )
  }

  return (
    <Suspense fallback={null}>
      <LazyCopilotImmersiveEmbedded
        payload={embeddedPayload}
        pendingOpenEvent={pendingOpenEvent}
        onPendingEventHandled={onPendingEventHandled}
      />
    </Suspense>
  )
}

const LazyCopilotImmersiveEmbedded = lazy(async () => {
  const mod = await import('@github-ui/copilot-immersive-embedded/CopilotImmersiveEmbedded')
  return {default: mod.CopilotImmersiveEmbedded}
})

const LazyChatAppEmbedded = lazy(async () => {
  try {
    const mod = await import('@github-ui/copilot-chat-app-embedded/ChatAppEmbeddedPartial')
    return {default: mod.ChatAppEmbeddedPartial}
  } catch (error) {
    failPendingCopilotUiInitialization('embedded_chat', 'registration')
    throw error
  }
})
